import { useEffect, useState } from "react";
import { HiSearch, HiPlus, HiPencil, HiEye, HiSwitchHorizontal } from "react-icons/hi";
import { makeRequestAuth } from "../../_config/api";
import NoData from "../../utils/DataNotFound";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../../utils/currencyFormat";

export default function BankListPage() {
    const navigate = useNavigate()
    const [search, setSearch] = useState("");
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 10;
    const [debounceTimeout, setDebounceTimeout] = useState(null);

    // Fetch API
    const fetchBanks = async (page = 1, keyword = "") => {
        try {
            setLoading(true);
            const res = await makeRequestAuth("post", "/admin/getLoanList", { pageNo: page, size: pageSize, keyWord: keyword });

            if (res?.error === false) {
                setBanks(res.data.list);
                setTotalPages(Math.ceil(res.data.total / pageSize));
            } else {
                setBanks([]);
                setTotalPages(1);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
            setBanks([]);
            setTotalPages(1);
        }
        setLoading(false);
    };

    // Initial fetch & page change
    useEffect(() => {
        fetchBanks(pageNo, search);
    }, [pageNo]);

    // Handle search input with debounce
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);

        if (debounceTimeout) clearTimeout(debounceTimeout);

        const timeout = setTimeout(() => {
            setPageNo(1); // reset to first page
            fetchBanks(1, value);
        }, 500); // 500ms delay

        setDebounceTimeout(timeout);
    };

    const toggleBankStatus = async (id, currentStatus) => {
        try {
            const res = await makeRequestAuth("post", "/admin/bankLoanStatusToggel", { id });
            if (!res.error) {
                // Update local state instantly
                setBanks(prev =>
                    prev.map(bank =>
                        bank._id === id ? { ...bank, isDisable: !currentStatus } : bank
                    )
                );

                // Friendly toast message
                showSuccessToast(!currentStatus
                    ? "✅ Bank loan has been disabled!"
                    : "✅ Bank loan is now enabled!"
                );
            } else {
                showErrorToast(res.message);
            }
        } catch (err) {
            console.error(err);
            showErrorToast(err.message || "Something went wrong!");
        }
    };


    const prevPage = () => pageNo > 1 && setPageNo(pageNo - 1);
    const nextPage = () => pageNo < totalPages && setPageNo(pageNo + 1);

    return (
        <div className="w-full p-6 space-y-6">
            <ToastContainer />
            {/* Header */}
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-[#434646]">
                    Loans
                </h2>
            </div>



            {/* Search */}
            <div className="flex items-center gap-3 bg-white p-4 rounded-2xl shadow">
                <div className="flex items-center gap-2 border p-2 rounded-xl w-full">
                    <HiSearch />
                    <input
                        placeholder="Search Bank..."
                        className="outline-none w-full"
                        value={search}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>

            {/* Loader */}
            {loading && <p className="text-center text-[#434646]">Loading...</p>}

            {/* Bank Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {!loading && banks.length > 0
                    ? banks.map((bank) => (
                        <div key={bank?._id} className={`border rounded-2xl p-5 shadow relative 
                            ${bank.isDisable ? "border-red-500 opacity-50  bg-gray-50" : "bg-white"}`}>

                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-3">
                                    <img src={bank.bankLogo} alt="logo" className="w-12 h-12 object-contain" />
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#434646]">{bank.name}</h3>
                                        {bank.recommended && (
                                            <span className="text-xs bg-[#4FFE49] text-white px-2 py-1 rounded">Recommended</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-3">
                                    <button onClick={() => navigate("/loans/edit", { state: bank })}
                                        className="p-2 rounded-xl bg-[#749EED] text-white shadow" disabled={bank.isDisable}><HiPencil />
                                    </button>
                                    <button
                                        onClick={() => toggleBankStatus(bank?._id, bank?.isDisable)}
                                        className="p-2 rounded-xl shadow"
                                    >
                                        <HiSwitchHorizontal
                                            size={20}
                                            className={bank?.isDisable ? "text-red-500" : "text-green-500"}
                                        />
                                    </button>

                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 text-center mt-4 text-[#434646]">
                                <div><p className="font-semibold text-lg">{bank.interest}%</p><p className="text-sm">Interest</p></div>
                                <div><p className="font-semibold text-lg">{formatCurrency(Number(bank.loanAmount))}</p><p className="text-sm">Loan Amount</p></div>
                                <div><p className="font-semibold text-lg">{bank.tenure} Yr</p><p className="text-sm">Tenure</p></div>
                                <div><p className="font-semibold text-lg">{formatCurrency(Number(bank.monthly_emi))}</p><p className="text-sm">Monthly EMI</p></div>
                            </div>

                            <p className="mt-3 text-[#434646]">{bank.details || "Fast loan disbursal within days"}</p>

                            <div className="mt-4 flex justify-between items-center">
                                <p className="font-semibold text-[#434646]">{formatCurrency(Number(bank.reward))} Reward</p>
                                <span className="text-[#FE4D4D] font-semibold text-lg">{bank.phone}</span>
                            </div>
                        </div>

                    ))
                    : !loading && (
                        <div className="col-span-2 text-center text-[#434646] py-10">
                            <NoData title="No Bank Loan Founds!" />
                        </div>
                    )
                }
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-4 mt-6">
                <button onClick={prevPage} disabled={pageNo === 1} className="px-4 py-2 border rounded-xl disabled:opacity-50">Prev</button>
                <span>Page {pageNo} / {totalPages}</span>
                <button onClick={nextPage} disabled={pageNo === totalPages} className="px-4 py-2 border rounded-xl disabled:opacity-50">Next</button>
            </div>
        </div >
    );
}